import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  ICON_MAX,
  ICON_SUGGESTIONS,
  KEY_MAX,
  NAME_MAX,
  isActive,
  toKey,
  validatePayment,
} from "./paymentUtils";

// No celular o diálogo vira "bottom sheet" (preso embaixo, entra de baixo)
const SHEET_CLASSES =
  "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:left-0 max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:zoom-out-100";

const inputClass =
  "h-11 w-full rounded-xl border bg-card px-3.5 text-base text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-60 md:text-sm";

const EMPTY = { id: "", name: "", icon: "", active: true };

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-xs font-medium text-destructive" role="alert">
      {message}
    </p>
  );
}

/**
 * Criar/editar meio de pagamento. `payment` null = criação.
 * `payments` (lista completa) serve para barrar chave repetida.
 * `serverErrors` traz erros de campo devolvidos pela API (PAY003/PAY006).
 */
function PaymentMethodFormModal({ open, onOpenChange, payment, payments = [], serverErrors, onSave, loading }) {
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  // Na criação a chave acompanha o nome até ser editada à mão
  const [keyTouched, setKeyTouched] = useState(false);
  const nameRef = useRef(null);
  const keyRef = useRef(null);
  const isEdit = Boolean(payment);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setKeyTouched(Boolean(payment));
    setFormData(
      payment
        ? {
            id: payment.id ?? "",
            name: payment.name ?? "",
            icon: payment.icon ?? "",
            active: isActive(payment),
          }
        : EMPTY
    );
  }, [payment, open]);

  useEffect(() => {
    if (!serverErrors) return;
    setErrors((prev) => ({ ...prev, ...serverErrors }));
    if (serverErrors.id) keyRef.current?.focus();
  }, [serverErrors]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "name" && !keyTouched) next.id = toKey(value);
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === "name" && !keyTouched && errors.id) setErrors((prev) => ({ ...prev, id: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const others = payments.filter((p) => String(p.id) !== String(payment?.id));
    const { errors: nextErrors, payload } = validatePayment(formData, others);
    setErrors(nextErrors);
    if (!payload) {
      if (nextErrors.name) nameRef.current?.focus();
      else if (nextErrors.id) keyRef.current?.focus();
      return;
    }
    onSave(payload);
  };

  const keyChanged = isEdit && toKey(formData.id) !== String(payment.id).toLowerCase();

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent
        className={cn("max-h-[90vh] max-w-md gap-0 overflow-y-auto rounded-2xl bg-background p-6", SHEET_CLASSES)}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameRef.current?.focus();
        }}
      >
        <div className="mx-auto -mt-2 mb-4 h-1.5 w-12 rounded-full bg-border sm:hidden" aria-hidden="true" />
        <DialogHeader className="space-y-0.5 pb-4 pr-8 text-left">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            {isEdit ? "Editar Meio de Pagamento" : "Novo Meio de Pagamento"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? "Atualize os dados do meio de pagamento."
              : "Adicione uma forma de pagamento aceita nos pedidos."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <Label htmlFor="payment-name" className="mb-1 block text-xs font-semibold">
              Nome <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <div
                className={cn(
                  "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-surface-container text-lg",
                  !formData.icon && "font-display text-sm font-bold text-primary"
                )}
                aria-hidden="true"
              >
                {formData.icon || (formData.name.trim().charAt(0).toUpperCase() || "?")}
              </div>
              <input
                id="payment-name"
                ref={nameRef}
                value={formData.name}
                maxLength={NAME_MAX}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ex.: Vale-refeição"
                autoComplete="off"
                disabled={loading}
                aria-invalid={Boolean(errors.name) || undefined}
                aria-describedby={errors.name ? "payment-name-error" : undefined}
                className={cn(inputClass, "font-medium", errors.name ? "border-destructive" : "border-border")}
              />
            </div>
            <FieldError id="payment-name-error" message={errors.name} />
          </div>

          <div>
            <Label htmlFor="payment-key" className="mb-1 block text-xs font-semibold">
              Chave <span className="text-destructive">*</span>
            </Label>
            <input
              id="payment-key"
              ref={keyRef}
              value={formData.id}
              maxLength={KEY_MAX}
              onChange={(e) => {
                setKeyTouched(true);
                handleChange("id", e.target.value.toLowerCase().replace(/\s/g, "_"));
              }}
              onBlur={() => handleChange("id", toKey(formData.id))}
              placeholder="ex.: vale_refeicao"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={loading}
              aria-invalid={Boolean(errors.id) || undefined}
              aria-describedby="payment-key-hint"
              className={cn(inputClass, "font-mono", errors.id ? "border-destructive" : "border-border")}
            />
            {errors.id ? (
              <FieldError id="payment-key-hint" message={errors.id} />
            ) : (
              <p id="payment-key-hint" className="mt-1 text-[11px] text-muted-foreground">
                {keyChanged
                  ? "Atenção: a chave não pode ser trocada se o meio já foi usado em pedidos."
                  : "Identificador enviado no pedido. Letras minúsculas, números, _ ou -."}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="payment-icon" className="mb-1 block text-xs font-semibold">
              Ícone
            </Label>
            <input
              id="payment-icon"
              value={formData.icon}
              maxLength={ICON_MAX}
              onChange={(e) => handleChange("icon", e.target.value)}
              placeholder="Emoji (opcional)"
              autoComplete="off"
              disabled={loading}
              aria-invalid={Boolean(errors.icon) || undefined}
              aria-describedby={errors.icon ? "payment-icon-error" : undefined}
              className={cn(inputClass, errors.icon ? "border-destructive" : "border-border")}
            />
            <FieldError id="payment-icon-error" message={errors.icon} />
            <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Sugestões de ícone">
              {ICON_SUGGESTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleChange("icon", formData.icon === emoji ? "" : emoji)}
                  disabled={loading}
                  aria-pressed={formData.icon === emoji}
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-lg border text-base transition-colors disabled:opacity-60",
                    formData.icon === emoji
                      ? "border-primary bg-secondary ring-2 ring-primary/15"
                      : "border-border bg-card hover:bg-surface-container"
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <label
            htmlFor="payment-active"
            className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card/80 p-3"
          >
            <span className="pr-1">
              <span className="block text-xs font-semibold text-foreground">Ativo</span>
              <span className="mt-0.5 block text-[11px] leading-tight text-muted-foreground">
                Inativo não aparece no cardápio e não pode ser usado em novos pedidos.
              </span>
            </span>
            <Switch
              id="payment-active"
              checked={formData.active}
              onCheckedChange={(checked) => handleChange("active", checked)}
              disabled={loading}
            />
          </label>

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full border border-border bg-surface-container px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Salvando..." : isEdit ? "Atualizar" : "Criar"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PaymentMethodFormModal;
